import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DOCUMENT_PLURALS, PageHead, StatusBadge } from '../components/bits.tsx';
import { Button, EmptyState, Panel, TableSkeleton } from '../components/ui/index.tsx';
import { api } from '../lib/api.ts';
import { formatDate, formatMoney, relativeDays } from '../lib/money.ts';
import { useSession } from '../state/session.tsx';
import type { DocumentListItem, DocumentStatus, DocumentType } from '../lib/types.ts';

interface ListResponse {
  documents: DocumentListItem[];
  total: number;
  limit: number;
  offset: number;
}

const LEDES: Partial<Record<DocumentType, string>> = {
  invoice: 'What you have billed, and what is still owed.',
  quotation: 'Estimates you have sent. Convert the accepted ones into invoices.',
  proforma: 'Advance bills issued before the sale is final.',
  purchase_order: 'What you have committed to buy from your vendors.',
  delivery_challan: 'Goods dispatched, with or without an invoice attached.',
  credit_note: 'Reversals against invoices already issued.',
};

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'sent', label: 'Sent' },
  { value: 'partially_paid', label: 'Part paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function DocumentList({ type }: { type: DocumentType }) {
  const { business, canWrite } = useSession();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');

  const status = searchParams.get('status') ?? '';
  const showsMoney = type !== 'delivery_challan';

  const { data, isLoading } = useQuery({
    queryKey: ['documents', business?.id, type, status, search],
    queryFn: () =>
      api.get<ListResponse>('/api/documents', {
        type,
        status: status || undefined,
        search: search || undefined,
        limit: 100,
      }),
    enabled: Boolean(business),
  });

  const documents = data?.documents ?? [];
  const outstanding = documents.reduce((sum, document) => sum + document.balanceMinor, 0);

  return (
    <>
      <PageHead
        title={DOCUMENT_PLURALS[type]}
        lede={LEDES[type]}
        actions={
          canWrite && (
            <Button variant="primary" onClick={() => navigate(`/documents/new?type=${type}`)}>
              New {DOCUMENT_PLURALS[type].toLowerCase().replace(/s$/, '')}
            </Button>
          )
        }
      />

      <div className="toolbar">
        <input
          className="input toolbar__search"
          type="search"
          placeholder="Search number, reference or customer"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label={`Search ${DOCUMENT_PLURALS[type].toLowerCase()}`}
        />
        {showsMoney && (
          <select
            className="select"
            style={{ width: 'auto' }}
            value={status}
            onChange={(event) => {
              const next = new URLSearchParams(searchParams);
              if (event.target.value) next.set('status', event.target.value);
              else next.delete('status');
              setSearchParams(next, { replace: true });
            }}
            aria-label="Filter by status"
          >
            {STATUS_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
        )}
        {outstanding !== 0 && showsMoney && (
          <p className="push muted" style={{ fontSize: 'var(--text-sm)' }}>
            Outstanding in view:{' '}
            <strong className="numeric" style={{ color: 'var(--ink)' }}>
              {formatMoney(outstanding, business?.currency ?? 'INR')}
            </strong>
          </p>
        )}
      </div>

      <Panel flush>
        {isLoading ? (
          <TableSkeleton rows={6} columns={6} />
        ) : documents.length === 0 ? (
          <EmptyState
            mark="▤"
            title={search || status ? 'Nothing matches those filters' : `No ${DOCUMENT_PLURALS[type].toLowerCase()} yet`}
            lede={
              search || status
                ? 'Try clearing the search or status filter.'
                : LEDES[type] ?? 'Create the first one to get started.'
            }
            action={
              canWrite && !search && !status ? (
                <Button variant="primary" onClick={() => navigate(`/documents/new?type=${type}`)}>
                  Create one
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>{type === 'purchase_order' ? 'Vendor' : 'Customer'}</th>
                  <th>Issued</th>
                  {showsMoney && <th>Due</th>}
                  {showsMoney && <th className="num">Total</th>}
                  {showsMoney && <th className="num">Balance</th>}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((document) => (
                  <tr
                    key={document.id}
                    className="row-link"
                    tabIndex={0}
                    onClick={() => navigate(`/documents/${document.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') navigate(`/documents/${document.id}`);
                    }}
                  >
                    <td className="strong">{document.number}</td>
                    <td className="truncate" style={{ maxWidth: '18rem' }}>
                      {document.partyName ?? <span className="faint">Unassigned</span>}
                    </td>
                    <td className="muted">{formatDate(document.issueDate)}</td>
                    {showsMoney && (
                      <td className="muted">
                        {document.dueDate ? (
                          <span
                            style={
                              document.status === 'overdue' ? { color: 'var(--bad)' } : undefined
                            }
                          >
                            {relativeDays(document.dueDate)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    )}
                    {showsMoney && (
                      <td className="num">{formatMoney(document.totalMinor, document.currency)}</td>
                    )}
                    {showsMoney && (
                      <td className="num strong">
                        {document.balanceMinor === 0 ? (
                          <span className="faint">—</span>
                        ) : (
                          formatMoney(document.balanceMinor, document.currency)
                        )}
                      </td>
                    )}
                    <td>
                      <StatusBadge status={document.status as DocumentStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {data && data.total > documents.length && (
        <p className="faint" style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)' }}>
          Showing {documents.length} of {data.total}. Narrow the search to see the rest.
        </p>
      )}
    </>
  );
}
